/**
 * The connectivity preview of a script (D42): at each step, what is open,
 * what each source reaches, and where liquid could be. A preview, not a
 * simulation: no pressure, flow, volume or time, only what connects to what.
 *
 * The design is taken apart into places (each line, cut at the parts mounted
 * on it (D40), and each component, split into the groups of ports it joins
 * inside in its current state) and joined twice:
 *
 * - **Gas** passes everywhere a line and a component's inside join, and
 *   through a hydrophobic vent's membrane.
 * - **Liquid** passes the same way, but not through a vent's membrane.
 *
 * A gas region is at a source (pressure, vacuum, flow), vented to
 * atmosphere, or sealed. Liquid moves in a liquid region when something can
 * drive it (pressure on liquid, or vacuum with liquid that can follow, from a
 * collapsible bag or a vented space) and somewhere can take it (a bag, a
 * vented or evacuated space, a vent membrane). It then fills the through
 * route: branches that lead nowhere are left with their air, which is what a
 * designer needs to see.
 *
 * Gas blows through too. Where gas is driven in (pressure or flow, or air
 * drawn in by a vacuum) and has a way out, and meets liquid in the lines
 * before it meets a vessel holding liquid, the lines on its route empty: the
 * liquid goes on into a vessel on the way out, or out of the circuit. Gas
 * that meets a vessel holding liquid first is driving that liquid, above.
 */
import type { DesignDocument, EdgeDocument, NodeDocument, TemplateDocument } from "../design/document";
import type { Comparison, ConditionSetDocument, Profile, RunPump, RunSetpoint, RunSource, ScriptDocument, Source, SourceKind, SourceSetting, Step } from "../design/protocol";
import { AIR } from "../design/protocol";
import { profilePeak, profileReaches, profileValues, secondsText } from "./profiles";

export type GasState = "pressure" | "vacuum" | "flow" | "vented" | "sealed";

export interface PlacePreview {
  gas: GasState;
  /** A fluid here after the step, or null for air. */
  liquid: string | null;
}

/** One stretch of a line: the whole of it, or the part between two mounted parts. */
export interface SpanPreview extends PlacePreview {
  /** Fractions of the line's hydraulic length, from its start. */
  from: number;
  to: number;
}

export interface PreviewFinding {
  code: "air_trapped" | "vent_wetted" | "no_way_out" | "unbound_source" | "unknown_state" | "bubble_point" | "run_out_unclear" | "regulator_starved" | "endpoint_unreachable";
  message: string;
  refs: string[];
  /** A bubble point reached by a profile: the source, the level it reaches it at (D53), and the rated point (D54). */
  source?: string;
  value?: number;
  point?: number;
}

/** A component in a step: whether it passes flow, what fills it. */
export interface NodePreview extends PlacePreview {
  passes: boolean;
  /** A switching valve (D66): the port its state joins to common, and the gas on that route. */
  switched?: { port: string; gas: GasState };
}

/** A port with a source on it: what the source is set to in this step. */
export interface PortPreview {
  /** The profile it runs in this step, if it runs one (D52): the magnitude is then its peak. */
  profile?: RunSource;
  /** The external port, `node.port`. */
  port: string;
  source: string;
  setting: string;
  kind: SourceKind;
  /** Pascal gauge, where it has one. */
  magnitude: number | null;
  /** No source on it: open at its end to atmosphere (D59). */
  unsourced?: boolean;
  /** A drawn supply with no source in the condition set: live at what it is rated to give, until one says otherwise. */
  rated?: boolean;
}

/**
 * What an instrument reads in a step, only as surely as the preview knows
 * it: `exact` where nothing it cannot say decides it (a pressure where
 * nothing flows, a detector's liquid or air, a pump's set rate through a
 * flow meter); `bound` where something flows and the preview can say only
 * between what (a pressure under flow); `unknown` where it cannot say. A
 * solved value from the Simulation step would be a fourth, later.
 */
export interface Readout {
  /** Its tag after ISA 5.1, by what it observes: "PI-3", "FI-1". */
  tag: string;
  observes: string;
  certainty: "exact" | "bound" | "unknown";
  /** SI: pascal gauge (vacuum negative), m³/s. An exact value, or a bound's limits. */
  value?: number;
  low?: number;
  high?: number;
  /** What it reads without a number: a detector's liquid or air; flowing, of no known rate; a sealed space. */
  state?: "liquid" | "air" | "flowing" | "sealed";
  /** The step's endpoint on it, as an alarm limit; and whether the step can never meet it. */
  limit?: { comparison: Comparison; value: number; unreachable?: boolean };
}

/** ISA 5.1 letters by what an instrument observes: the measured variable, then I for indicating. */
const ISA_LETTERS: Readonly<Record<string, string>> = {
  pressure: "PI",
  flow_rate: "FI",
  air_in_line: "LS",
  conductivity: "AI",
  ph: "AI",
  concentration: "AI",
  temperature: "TI",
};

/** An instrument's tag: its letters, and the number its label ends in, else its identifier's. */
export function instrumentTag(node: Pick<NodeDocument, "local_id" | "label">, letters: string): string {
  const number = /(\d+)\s*$/.exec(node.label ?? "")?.[1] ?? /(\d+)$/.exec(node.local_id)?.[1];
  return number ? `${letters}-${number}` : letters;
}

/** Whether `value` meets `comparison` against `target`. */
function meets(value: number, comparison: Comparison, target: number): boolean {
  switch (comparison) {
    case "lt":
      return value < target;
    case "le":
      return value <= target;
    case "gt":
      return value > target;
    case "ge":
      return value >= target;
  }
}

/** A regulator in a step (D65, D66): what it is asked to hold, and what its outlet gets. */
export interface RegulatorPreview {
  /** Its setpoint (pascal gauge), or a sweep's furthest; null with none, its outlet relieved. */
  asked: number | null;
  /** What its outlet holds where it regulates (pascal gauge): the setpoint, or less where the supply gives less. */
  held?: number;
  /** The sweep it runs in this step, if it runs one. */
  sweep?: RunSetpoint;
  /** What its outlet side is open to: pressure or vacuum where it regulates, else vented. */
  gas: GasState;
}

export interface StepPreview {
  /** Step zero: before the first step. */
  stepZero?: boolean;
  /** Step zero of a script that starts after another: what is where came from there (D44). */
  inherited?: boolean;
  /** Port components joined by lines on every side: open connections, never sources. */
  inlinePorts?: readonly string[];
  /** Ports with a source, by port component. */
  ports: Readonly<Record<string, PortPreview>>;
  /** Each regulator: what it is asked to hold, and what its outlet gets. */
  regulators?: Readonly<Record<string, RegulatorPreview>>;
  /** Each sensor's reading in the step, and the step's endpoint on it. */
  readouts?: Readonly<Record<string, Readout>>;
  /** What each fluid is called, for drawing: its description, else its name. */
  fluidLabels: Readonly<Record<string, string>>;
  /** The colours fluids were given, where they were (the rest take the preview's own). */
  fluidColours?: Readonly<Record<string, string>>;
  /** What each vessel holds full (m³): its nominal volume, or a chamber's volume, where given. */
  capacities?: Readonly<Record<string, number>>;
  /** How much each vessel holds (m³): at the start, the set-up's; after a step, as tracked net of hold-up (D60), where known. */
  volumes: Readonly<Record<string, number>>;
  /** Each stateful component's state after the step's actions. */
  states: Readonly<Record<string, string>>;
  /** Each source's setting. */
  sources: Readonly<Record<string, string>>;
  /** Components: whether each passes flow now, and what fills it. */
  nodes: Readonly<Record<string, NodePreview>>;
  /** Lines, in spans. */
  edges: Readonly<Record<string, readonly SpanPreview[]>>;
  /** What each pump does in this step, for the drawing ("200 mL @ 10 mL/s"). Set by the step's owner. */
  operations?: Readonly<Record<string, string>>;
  /** Components liquid moved through or into in this step: where a volume is no longer what it was. */
  moved?: readonly string[];
  /**
   * Where the step ran a vessel dry, as it meant to: it ends on an air
   * detector seeing air, with one vessel the liquid can have come from and
   * at most one it can have gone to (D60). The line up to the detector is
   * cleared.
   */
  ranOut?: readonly { sensor?: string; from: string; to: string | null }[];
  /** Vessels that took the liquid gas swept out of the lines. */
  collected?: readonly string[];
  /** The liquid region each line span (`edge:index`) and component is in at the end of the step. */
  regions?: Readonly<Record<string, string>>;
  findings: readonly PreviewFinding[];
}

// --- Components' insides ------------------------------------------------------

/**
 * A port joined by lines on every side it has: a connection fluid passes
 * straight through (a tubing port between two lines), not where a source or
 * the atmosphere meets the circuit. It is open; no source can sit on it.
 */
export function isInlinePort(design: DesignDocument, node: NodeDocument): boolean {
  if (node.role !== "port") return false;
  const lined = new Set((design.edges ?? []).flatMap((edge) => [edge.from_port, edge.to_port]));
  const ports = flowPorts(node);
  return ports.length > 1 && ports.every((port) => lined.has(`${node.local_id}.${port}`));
}

const top = (ref: string) => ref.split(".")[0]!;

/**
 * Components whose external port, with no line and no source on it, is open
 * to atmosphere (D59): a port left open, a filter drawing air in or letting it
 * out. An aseptic connector's free end is not one: until mated it is closed.
 * Any other unsourced external port is sealed.
 */
const OPEN_ENDED: ReadonlySet<string> = new Set(["port", "filter"]);

/** Vessels: an external port of theirs with no line and no source on it opens their headspace to atmosphere. */
const VESSEL_ROLES: ReadonlySet<string> = new Set(["reservoir", "chamber"]);

/** A port component's external port, with no line and no source on it: open to atmosphere (D59). */
function openEnd(design: DesignDocument, node: NodeDocument, sourced: ReadonlySet<string>): string | null {
  if (!OPEN_ENDED.has(node.role)) return null;
  const lined = new Set((design.edges ?? []).flatMap((edge) => [edge.from_port, edge.to_port]));
  const port = (node.ports ?? []).find((candidate) => {
    const ref = `${node.local_id}.${candidate.local_id}`;
    return candidate.is_external && candidate.kind !== "instrument" && !lined.has(ref) && !sourced.has(ref);
  });
  return port ? `${node.local_id}.${port.local_id}` : null;
}

/** The ports fluid can use: not an instrument's connections. */
function flowPorts(node: NodeDocument): string[] {
  return (node.ports ?? []).filter((port) => port.kind !== "instrument").map((port) => port.local_id);
}

/**
 * A switching valve's way through in a state (D66): a valve with a `common`
 * port names each state for the port it joins to it, as a 3/2 solenoid's
 * `supply` and `exhaust`. Null for any other valve or state.
 */
export function switchedTo(node: NodeDocument, state: string | undefined): string | null {
  if (node.role !== "valve" || !state || state === "common") return null;
  const ports = new Set((node.ports ?? []).map((port) => port.local_id));
  return ports.has("common") && ports.has(state) ? state : null;
}

/** Whether a component in this state joins its ports inside. */
export function passes(node: NodeDocument, state: string | undefined): boolean {
  if (!state) return true;
  switch (node.role) {
    case "valve":
      // A switching valve always joins common to one port or another.
      return /^open/.test(state) || switchedTo(node, state) !== null;
    case "pump":
      // A peristaltic head occludes the tube when stopped.
      return state !== "off";
    case "aseptic_connector":
      return state === "connected";
    default:
      return true;
  }
}

/** Whether a vessel keeps its shape as it empties (`wall: rigid`, D50), as the node or its template says. */
export function isRigid(node: NodeDocument, templates: Readonly<Record<string, TemplateDocument>>): boolean {
  const template = node.template_version_id ? templates[node.template_version_id] : undefined;
  const wall = node.parameters?.wall ?? template?.parameters?.find((parameter) => parameter.key === "wall")?.default;
  return wall === "rigid";
}

/**
 * A component's numeric parameter as it stands: its own, else its template's
 * default; null where neither is a number. A template seeded before the
 * library read `3.2e5` as a number holds it as a string: read as its number.
 */
export function parameterValue(node: NodeDocument, templates: Readonly<Record<string, TemplateDocument>>, key: string): number | null {
  const template = node.template_version_id ? templates[node.template_version_id] : undefined;
  const value = node.parameters?.[key] ?? template?.parameters?.find((parameter) => parameter.key === key)?.default;
  const number = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
  return typeof number === "number" && Number.isFinite(number) ? number : null;
}

/** What a sensor observes (`air_in_line`, `pressure`, …), as the node or its template says. */
export function observesOf(node: NodeDocument, templates: Readonly<Record<string, TemplateDocument>>): string | undefined {
  const template = node.template_version_id ? templates[node.template_version_id] : undefined;
  const value = node.parameters?.observes ?? template?.parameters?.find((p) => p.key === "observes")?.default;
  return typeof value === "string" ? value : undefined;
}

/** What a step means to run dry: past an air detector (D60), or a vessel it names (D62). */
export type RunOut = { sensor: string; vessel?: undefined } | { vessel: string; sensor?: undefined };

/** The ways a step's endpoint says it runs something dry: air detectors seeing air, and vessels run empty. */
export function runOuts(design: DesignDocument, templates: Readonly<Record<string, TemplateDocument>>, step: Step): RunOut[] {
  const vessels = (step.finish?.conditions ?? []).flatMap((condition) => (condition.kind === "vessel_empty" ? [{ vessel: condition.vessel }] : []));
  return [...vessels, ...runOutSensors(design, templates, step).map((sensor) => ({ sensor }))];
}

/**
 * The air detectors whose seeing air ends a step (D60): what the step means
 * to do is run the liquid past them.
 */
export function runOutSensors(
  design: DesignDocument,
  templates: Readonly<Record<string, TemplateDocument>>,
  step: Step,
): string[] {
  const sensors: string[] = [];
  for (const condition of step.finish?.conditions ?? []) {
    if (condition.kind !== "sensed" || (condition.measure ?? "reading") !== "reading") continue;
    const air = (condition.comparison === "ge" && condition.value <= 1 && condition.value > 0) || (condition.comparison === "gt" && condition.value >= 0 && condition.value < 1);
    const node = design.nodes?.find((candidate) => candidate.local_id === condition.sensor);
    if (air && node && observesOf(node, templates) === "air_in_line") sensors.push(node.local_id);
  }
  return sensors;
}

/** A filter's bubble point wetted (pascal), as the node or its template states it; null where it does not. */
export function bubblePoint(node: NodeDocument, templates: Readonly<Record<string, TemplateDocument>>): number | null {
  return parameterValue(node, templates, "bubble_point");
}

/** A vessel's ports on its headspace: a vent, or a top port. Its liquid lies against the rest. */
function onHeadspace(node: NodeDocument, port: string): boolean {
  return port.startsWith("top") || node.ports?.find((candidate) => candidate.local_id === port)?.kind === "vent";
}

/**
 * Port groups joined inside a component, for gas or for liquid. A rigid
 * vessel holding liquid (`wet`) is two spaces to gas: its headspace and what
 * lies beneath its liquid, which gas cannot pass through. The headspace is
 * group 0, the vessel's own place.
 */
function groups(node: NodeDocument, state: string | undefined, liquid: boolean, wet = false): string[][] {
  const ports = flowPorts(node);
  if (ports.length === 0) return [];
  if (wet && !liquid) {
    const above = ports.filter((port) => onHeadspace(node, port));
    const below = ports.filter((port) => !onHeadspace(node, port));
    if (above.length > 0 && below.length > 0) return [above, below];
  }
  // A hydrophobic vent passes gas, not liquid, through its membrane.
  if (node.role === "vent" && liquid) return ports.map((port) => [port]);
  // A regulator is a boundary: what its outlet holds is its own (D66).
  if (node.role === "regulator") return ports.map((port) => [port]);
  // A filter's two sides, joined across its membrane (see `topology`).
  const sides = filterSides(node);
  if (sides) return sides;
  // A switching valve joins common to the port its state names; the rest are shut.
  const switched = switchedTo(node, state);
  if (switched) return [["common", switched], ...ports.filter((port) => port !== "common" && port !== switched).map((port) => [port])];
  return passes(node, state) ? [ports] : ports.map((port) => [port]);
}

/**
 * A filter's upstream side (its inlet, and a vent there) and its downstream
 * side (its outlet), where it names an outlet. Liquid crosses the membrane
 * between them; gas crosses it only while it is dry, as a wetted membrane
 * holds gas below its bubble point.
 */
function filterSides(node: NodeDocument): [string[], string[]] | null {
  if (node.role !== "filter") return null;
  const ports = flowPorts(node);
  const outlet = ports.filter((port) => port === "outlet");
  const upstream = ports.filter((port) => port !== "outlet");
  return outlet.length === 1 && upstream.length > 0 ? [upstream, outlet] : null;
}

// --- Union-find over places ----------------------------------------------------

class Regions {
  private parent = new Map<string, string>();
  add(place: string): void {
    if (!this.parent.has(place)) this.parent.set(place, place);
  }
  find(place: string): string {
    if (!this.parent.has(place)) return place;
    let root = place;
    while (this.parent.get(root) !== root) root = this.parent.get(root)!;
    let at = place;
    while (at !== root) {
      const next = this.parent.get(at)!;
      this.parent.set(at, root);
      at = next;
    }
    return root;
  }
  join(a: string, b: string): void {
    this.add(a);
    this.add(b);
    this.parent.set(this.find(a), this.find(b));
  }
}

interface Topology {
  places: string[];
  /** Place adjacency, for pruning dead ends. */
  links: Map<string, Set<string>>;
  regions: Regions;
  /** Where each node port is: the place of its group. */
  portPlace: Map<string, string>;
  /** Each filter's membrane: its two sides' places, by filter. */
  membranes: Map<string, [string, string]>;
}

const nodePlace = (node: string, group: number) => `n:${node}:${group}`;
const spanPlace = (edge: string, index: number) => `e:${edge}:${index}`;

/** Each line's spans: the whole line, or cut at the parts mounted on it (D40), with the parts between. */
function spansOf(edge: EdgeDocument, mounted: readonly NodeDocument[]): { from: number; to: number; after: NodeDocument | null }[] {
  const length = edge.hydraulic?.length ?? null;
  const on = mounted
    .filter((node) => node.mount?.edge === edge.local_id)
    .map((node) => ({ node, at: length && length > 0 ? Math.min(1, Math.max(0, node.mount!.distance / length)) : (node.mount!.along ?? 0.5) }))
    .sort((a, b) => a.at - b.at || a.node.local_id.localeCompare(b.node.local_id));
  const cuts = [0, ...on.map((entry) => entry.at), 1];
  return cuts.slice(0, -1).map((from, index) => ({ from, to: cuts[index + 1]!, after: on[index]?.node ?? null }));
}

function topology(
  design: DesignDocument,
  states: Readonly<Record<string, string>>,
  liquid: boolean,
  /** Rigid vessels holding liquid: to gas, their headspace and their bottom are apart. */
  wet: ReadonlySet<string> = new Set(),
  /** Filters whose wetted membrane holds the gas back: to gas, their two sides are apart. */
  held: ReadonlySet<string> = new Set(),
): Topology {
  const regions = new Regions();
  const links = new Map<string, Set<string>>();
  const places: string[] = [];
  const portPlace = new Map<string, string>();
  const link = (a: string, b: string) => {
    regions.join(a, b);
    if (!links.has(a)) links.set(a, new Set());
    if (!links.has(b)) links.set(b, new Set());
    links.get(a)!.add(b);
    links.get(b)!.add(a);
  };
  const membranes = new Map<string, [string, string]>();
  const nodes = design.nodes ?? [];
  for (const node of nodes) {
    groups(node, states[node.local_id], liquid, wet.has(node.local_id)).forEach((group, index) => {
      const place = nodePlace(node.local_id, index);
      places.push(place);
      regions.add(place);
      for (const port of group) portPlace.set(`${node.local_id}.${port}`, place);
    });
    if (filterSides(node)) {
      const sides: [string, string] = [nodePlace(node.local_id, 0), nodePlace(node.local_id, 1)];
      membranes.set(node.local_id, sides);
      if (!held.has(node.local_id)) link(...sides);
    }
  }
  const mounted = nodes.filter((node) => node.mount);
  for (const edge of design.edges ?? []) {
    const spans = spansOf(edge, mounted);
    spans.forEach((span, index) => {
      const place = spanPlace(edge.local_id, index);
      places.push(place);
      regions.add(place);
      if (index === 0) {
        const at = portPlace.get(edge.from_port);
        if (at) link(place, at);
      }
      if (index === spans.length - 1) {
        const at = portPlace.get(edge.to_port);
        if (at) link(place, at);
      }
      // Through the part mounted between this span and the next, if it lets flow by.
      const part = span.after;
      if (part) {
        const through = flowPorts(part).slice(0, 2).map((port) => portPlace.get(`${part.local_id}.${port}`));
        if (through[0]) link(place, through[0]);
        if (through[1]) link(spanPlace(edge.local_id, index + 1), through[1]);
      }
    });
  }
  return { places, links, regions, portPlace, membranes };
}

// --- Stepping through a script ------------------------------------------------

/**
 * Where a profile leaves a source (D52): not one of its named settings, so
 * written as its kind and value, `~pressure:350000`, which every reader of a
 * setting name understands through `settingOf`.
 */
export function profiledSetting(profile: RunSource): string {
  return `~${profile.quantity}:${profileValues(profile).at(-1) ?? 0}`;
}

/** A source's setting by name: one of its own, or where a profile left it. */
export function settingOf(source: Source, name: string | undefined): SourceSetting | undefined {
  const own = source.settings.find((candidate) => candidate.name === name);
  if (own || !name) return own;
  const left = /^~(pressure|vacuum|flow):(.+)$/.exec(name);
  return left ? { name, kind: left[1] as SourceKind, magnitude: Number(left[2]) } : undefined;
}

/**
 * What a drawn supply gives with nothing in the condition set to say (D66):
 * its kind, from what its template says it supplies, at its rating (a
 * pressure supply's highest, a vacuum supply's deepest, as a negative gauge).
 * Null for a vent, or anything else.
 */
export function ratedSupply(node: NodeDocument, templates: Readonly<Record<string, TemplateDocument>>): { kind: "pressure" | "vacuum"; magnitude: number | null } | null {
  if (node.role !== "supply") return null;
  const template = node.template_version_id ? templates[node.template_version_id] : undefined;
  const supplies = node.parameters?.supplies ?? template?.parameters?.find((parameter) => parameter.key === "supplies")?.default;
  if (supplies === "pressure") return { kind: "pressure", magnitude: parameterValue(node, templates, "pressure_max") };
  if (supplies === "vacuum") {
    const deepest = parameterValue(node, templates, "vacuum_max");
    return { kind: "vacuum", magnitude: deepest === null ? null : -Math.abs(deepest) };
  }
  return null;
}

/**
 * Where a source can sit on a component: an external port of its, not an
 * instrument's connection; or, on a drawn supply, its own port (D66), marked
 * external or not, since that is where its air is.
 */
export function sourcePorts(node: NodeDocument): string[] {
  const ports = (node.ports ?? []).filter((port) => port.kind !== "instrument");
  const external = ports.filter((port) => port.is_external);
  return (external.length > 0 || node.role !== "supply" ? external : ports).map((port) => `${node.local_id}.${port.local_id}`);
}

/** The sources a supply's rating stands in for: told apart from the condition set's own. */
const RATED = new WeakSet<Source>();

/**
 * The condition set, with each drawn pressure or vacuum supply it gives no
 * source live at its rating (D66): a supply drawn is the instrument's air,
 * on until the condition set says what it gives. Unchanged where none is missing.
 */
export function liveConditions(
  design: DesignDocument,
  templates: Readonly<Record<string, TemplateDocument>>,
  conditions: ConditionSetDocument,
): ConditionSetDocument {
  const sources = conditions.sources ?? [];
  const bound = new Set(sources.map((source) => top(source.port)));
  const names = new Set(sources.map((source) => source.name));
  const added: Source[] = [];
  for (const node of design.nodes ?? []) {
    if (bound.has(node.local_id)) continue;
    const rated = ratedSupply(node, templates);
    const port = sourcePorts(node)[0];
    if (!rated || !port) continue;
    let name = node.local_id;
    for (let n = 2; names.has(name); n++) name = `${node.local_id}_${n}`;
    names.add(name);
    const source: Source = { name, port, settings: [{ name: "rated", kind: rated.kind, magnitude: rated.magnitude }], initial: "rated" };
    RATED.add(source);
    added.push(source);
  }
  return added.length ? { ...conditions, sources: [...sources, ...added] } : conditions;
}

/** Where each regulator's setpoint starts: where the parent left it, then the script's own step zero (D65). */
function startingSetpoints(script: ScriptDocument | null | undefined, from?: StartPoint): Record<string, number> {
  const setpoints: Record<string, number> = { ...(from?.regulated ?? {}) };
  for (const start of script?.initial_setpoints ?? []) setpoints[start.target] = start.value;
  return setpoints;
}

/**
 * Where a script starts from: each component's state, each source's
 * setting, and where liquid is. The design's and condition set's start, or,
 * for a script that starts after another, where that one ends (D44).
 */
export interface StartPoint {
  states: Readonly<Record<string, string>>;
  sources: Readonly<Record<string, string>>;
  /** Liquid by place, or by element (`element:<id>`) as initial fill names it. */
  liquid: ReadonlyMap<string, string>;
  /** Each regulator's setpoint (pascal gauge) where a step has set one (D65): it holds until a step changes it. */
  regulated?: Readonly<Record<string, number>>;
  /** Bags still holding gas above their liquid: the set-up's headspace, until a step runs the bag dry. */
  gassed?: readonly string[];
}

/** The bags the set-up starts with gas above their liquid. */
function gassedAtStart(conditions: ConditionSetDocument, from?: StartPoint): Set<string> {
  if (from) return new Set(from.gassed ?? []);
  return new Set((conditions.initial_fill ?? []).filter((fill) => (fill.headspace ?? 0) > 0).map((fill) => fill.target));
}

/** What the regulators hold in a step (D65, D66): each one's setpoint, and the sweeps the step runs. */
interface Regulation {
  setpoints: Readonly<Record<string, number>>;
  sweeps: readonly RunSetpoint[];
}

/** Where each component starts: its declared initial state, unless step zero says otherwise. */
export function startingStates(design: DesignDocument, script?: ScriptDocument | null): Record<string, string> {
  const states: Record<string, string> = {};
  for (const node of design.nodes ?? []) if (node.initial_state) states[node.local_id] = node.initial_state;
  for (const start of script?.initial_states ?? []) states[start.target] = start.state;
  return states;
}

/** Step zero of `script`: from `from` (or the design and condition set), with its own overrides. */
function startOf(
  design: DesignDocument,
  conditions: ConditionSetDocument,
  script: ScriptDocument | null | undefined,
  from?: StartPoint,
): { states: Record<string, string>; sources: Record<string, string>; liquid: Map<string, string> } {
  const states = from ? { ...from.states } : startingStates(design, null);
  for (const start of script?.initial_states ?? []) states[start.target] = start.state;
  const sources: Record<string, string> = from
    ? { ...from.sources }
    : Object.fromEntries((conditions.sources ?? []).map((source) => [source.name, source.initial]));
  // A source the parent never knew (added since) starts where the condition set says.
  for (const source of conditions.sources ?? []) if (!(source.name in sources)) sources[source.name] = source.initial;
  for (const start of script?.initial_sources ?? []) sources[start.source] = start.setting;
  const liquid = new Map(from ? from.liquid : initialLiquid(design, conditions));
  return { states, sources, liquid };
}

/**
 * A pump's state for an operation: on unless it is off, where its state set
 * has them. A pump without an on/off state set has nothing to show.
 */
function pumpState(design: DesignDocument, target: string, mode: string): string | null {
  const node = design.nodes?.find((candidate) => candidate.local_id === target);
  const set = node?.state_set ?? [];
  const wanted = mode === "off" ? "off" : "on";
  return set.includes(wanted) ? wanted : null;
}

function replay(design: DesignDocument, conditions: ConditionSetDocument, script: ScriptDocument, from?: StartPoint) {
  const { states, sources } = startOf(design, conditions, script, from);
  // A regulator holds its setpoint until a step changes it; a sweep leaves it where it ended.
  const regulated = startingSetpoints(script, from);
  // Pumps the step before started and did not keep running: they stop when it ends (D45).
  let stopping: string[] = [];
  return (script.steps ?? []).map((step) => {
    for (const target of stopping) {
      const off = pumpState(design, target, "off");
      if (off) states[target] = off;
    }
    stopping = [];
    for (const action of step.actions ?? []) {
      if (action.kind === "set_state") states[action.target] = action.state;
      else if (action.kind === "set_source") sources[action.source] = action.setting;
      else if (action.kind === "source_profile") sources[action.source] = profiledSetting(action);
      else if (action.kind === "setpoint" && action.quantity === "pressure") regulated[action.target] = action.value;
      else if (action.kind === "setpoint_profile") regulated[action.target] = profileValues(action).at(-1) ?? 0;
      else if (action.kind === "pump") {
        const state = pumpState(design, action.target, action.mode);
        if (state) states[action.target] = state;
        if (action.mode !== "off" && !action.keep_running) stopping.push(action.target);
      }
    }
    const profiles = (step.actions ?? []).filter((action): action is RunSource => action.kind === "source_profile");
    const sweeps = (step.actions ?? []).filter((action): action is RunSetpoint => action.kind === "setpoint_profile");
    return { states: { ...states }, sources: { ...sources }, profiles, regulation: { setpoints: { ...regulated }, sweeps }, step };
  });
}

const DRIVEN: readonly GasState[] = ["pressure", "vacuum", "flow"];

/** The preview after every step of `script`, in order. Before the first step is `initialPreview`. */
export function previewScript(
  design: DesignDocument,
  templates: Readonly<Record<string, TemplateDocument>>,
  conditions: ConditionSetDocument,
  script: ScriptDocument,
  from?: StartPoint,
): StepPreview[] {
  return runScript(design, templates, liveConditions(design, templates, conditions), script, from).previews;
}

/** The previews, and where the last step leaves things: the start of a script after this one. */
export function runScript(
  design: DesignDocument,
  templates: Readonly<Record<string, TemplateDocument>>,
  conditions: ConditionSetDocument,
  script: ScriptDocument,
  from?: StartPoint,
): { previews: StepPreview[]; end: StartPoint } {
  conditions = liveConditions(design, templates, conditions);
  const start = startOf(design, conditions, script, from);
  const liquidAt = start.liquid;
  const gassed = gassedAtStart(conditions, from);
  let end: StartPoint = { states: start.states, sources: start.sources, liquid: new Map(liquidAt), regulated: startingSetpoints(script, from), gassed: [...gassed] };
  const previews = replay(design, conditions, script, from).map(({ states, sources, profiles, regulation, step }) => {
    const runOut = runOuts(design, templates, step);
    const preview = previewState(design, templates, conditions, states, sources, liquidAt, true, false, profiles, runOut, regulation, gassed, step);
    // Liquid is where it got to, and gone from where gas cleared it: the next step starts from here.
    liquidAt.clear();
    for (const [place, fluid] of preview.liquidPlaces) liquidAt.set(place, fluid);
    // A bag run dry has let its gas go after its liquid.
    for (const { from: bag } of preview.result.ranOut ?? []) gassed.delete(bag);
    end = { states, sources, liquid: new Map(preview.liquidPlaces), regulated: regulation.setpoints, gassed: [...gassed] };
    return preview.result;
  });
  return { previews, end };
}

/**
 * Where a script starts that starts after `chain`, root first: each run from
 * where the one before it ended. Undefined for an empty chain.
 */
export function startAfter(
  design: DesignDocument,
  templates: Readonly<Record<string, TemplateDocument>>,
  conditions: ConditionSetDocument,
  chain: readonly ScriptDocument[],
): StartPoint | undefined {
  let from: StartPoint | undefined;
  for (const parent of chain) from = runScript(design, templates, conditions, parent, from).end;
  return from;
}

/**
 * The state before any step: initial states, initial settings, initial fill;
 * or, after a parent, where it left them. Volumes only when the fill is the
 * condition set's: the preview tracks none.
 */
export function initialPreview(
  design: DesignDocument,
  templates: Readonly<Record<string, TemplateDocument>>,
  conditions: ConditionSetDocument,
  script?: ScriptDocument | null,
  from?: StartPoint,
): StepPreview {
  conditions = liveConditions(design, templates, conditions);
  const start = startOf(design, conditions, script, from);
  const regulation = { setpoints: startingSetpoints(script, from), sweeps: [] };
  const { result } = previewState(design, templates, conditions, start.states, start.sources, start.liquid, false, !from, [], [], regulation);
  return { ...result, stepZero: true, ...(from ? { inherited: true } : {}) };
}

/** Where liquid is at the start, by element: a component's every place, a line's every span. */
function initialLiquid(design: DesignDocument, conditions: ConditionSetDocument): Map<string, string> {
  const at = new Map<string, string>();
  for (const fill of conditions.initial_fill ?? []) {
    if (fill.fluid === AIR) continue;
    at.set(`element:${fill.target}`, fill.fluid);
  }
  void design;
  return at;
}

function previewState(
  design: DesignDocument,
  templates: Readonly<Record<string, TemplateDocument>>,
  conditions: ConditionSetDocument,
  states: Readonly<Record<string, string>>,
  sources: Readonly<Record<string, string>>,
  liquidBefore: ReadonlyMap<string, string>,
  move = true,
  withVolumes = !move,
  /** The profiles sources run in this step (D52). */
  profiles: readonly RunSource[] = [],
  /** What the step means to run dry (D60, D62): past an air detector, or a vessel named. */
  runOut: readonly RunOut[] = [],
  /** What each regulator holds, and what it sweeps (D65, D66). */
  regulation: Regulation = { setpoints: {}, sweeps: [] },
  /** Bags holding gas above their liquid: run dry, it follows the liquid out. */
  gassed: ReadonlySet<string> = new Set(),
  /** The step previewed: its pumps' rates and its endpoints, for what instruments read. */
  step?: Step,
): { result: StepPreview; liquidPlaces: Map<string, string> } {
  const profileOf = new Map(profiles.map((profile) => [profile.source, profile]));
  const findings: PreviewFinding[] = [];
  // A pump's rate where the step sets one steady: a ramp's varies.
  const rateOf = new Map(
    (step?.actions ?? [])
      .filter((action): action is RunPump => action.kind === "pump" && action.mode !== "off" && action.mode !== "ramp" && typeof action.rate === "number")
      .map((action) => [action.target, action.rate!]),
  );
  // A rigid vessel holding liquid as the step begins keeps its headspace's gas
  // from what lies under the liquid: pressure on top drives the liquid out of
  // the bottom; it does not reach the lines beyond.
  const holdsLiquid = (node: NodeDocument) =>
    liquidBefore.has(`element:${node.local_id}`) || [...liquidBefore.keys()].some((place) => place.startsWith(`n:${node.local_id}:`));
  const wetVessels = new Set(
    (design.nodes ?? [])
      .filter((node) => (node.role === "chamber" || (node.role === "reservoir" && isRigid(node, templates))) && holdsLiquid(node))
      .map((node) => node.local_id),
  );
  const gas = topology(design, states, false, wetVessels);
  const liquid = topology(design, states, true);
  const nodes = new Map((design.nodes ?? []).map((node) => [node.local_id, node]));
  const elementOf = (place: string) => place.split(":")[1]!;

  // What each gas region is open to.
  const openTo = new Map<string, Set<GasState>>();
  // Every place something opens the gas to, and to what: the drawing reads them again as the step leaves things.
  const marked: [string, GasState][] = [];
  const mark = (place: string | undefined, state: GasState) => {
    if (!place) return;
    marked.push([place, state]);
    const root = gas.regions.find(place);
    if (!openTo.has(root)) openTo.set(root, new Set());
    openTo.get(root)!.add(state);
  };
  const bound = new Set<string>();
  // Gas places where gas is driven in, and where it can leave: for blowing through.
  const gasIn = new Set<string>();
  const gasVented = new Set<string>();
  const gasVacuum = new Set<string>();
  // The gas pressure a source puts across what it drives through (pascal): a
  // pressure's, or a vacuum's pull; a profile's furthest, and the profile.
  const gasDrive = new Map<string, { value: number; source: string; profile?: Profile; vacuum?: boolean }>();
  // Where each source or opening is: liquid can go there, and nowhere else it
  // is open to, because the rest of the region only shares its gas.
  const openings = new Set<string>();
  // What each opening is open to: its source's kind, or the air.
  const openingGas = new Map<string, GasState>();
  for (const source of conditions.sources ?? []) {
    const profile = profileOf.get(source.name);
    const peak = profile ? profilePeak(profile) : null;
    const setting = profile ? { name: "profile", kind: profile.quantity, magnitude: peak } : settingOf(source, sources[source.name]);
    const place = gas.portPlace.get(source.port);
    if (!place) {
      findings.push({ code: "unbound_source", message: `Source ${source.name} is on ${source.port}, which is not in the design`, refs: [] });
      continue;
    }
    bound.add(source.port);
    const at = liquid.portPlace.get(source.port);
    if (at) openings.add(at);
    const kind: SourceKind = setting?.kind ?? "sealed";
    if (at) openingGas.set(at, kind === "open" ? "vented" : kind);
    if (kind === "open") mark(place, "vented");
    else if (kind !== "sealed") mark(place, kind);
    if (kind === "open") gasVented.add(place);
    else if (kind === "vacuum") gasVacuum.add(place);
    else if (kind === "pressure" || kind === "flow") gasIn.add(place);
    if ((kind === "pressure" || kind === "vacuum") && typeof setting?.magnitude === "number")
      gasDrive.set(place, { value: Math.abs(setting.magnitude), source: source.name, ...(profile ? { profile } : {}), ...(kind === "vacuum" ? { vacuum: true } : {}) });
  }
  // Vent membranes, open external vents, a port's or filter's open end (D59),
  // and a vessel's external port left unconnected are to atmosphere.
  // Vessels open to the air through a port of their own: air takes the place of what leaves them.
  const airOpen = new Set<string>();
  for (const node of design.nodes ?? []) {
    for (const port of node.ports ?? []) {
      const ref = `${node.local_id}.${port.local_id}`;
      if (!port.is_external || bound.has(ref) || port.kind === "instrument") continue;
      const lined = (design.edges ?? []).some((edge) => edge.from_port === ref || edge.to_port === ref);
      if (lined) continue;
      if (port.kind === "vent" || node.role === "vent" || OPEN_ENDED.has(node.role) || VESSEL_ROLES.has(node.role)) {
        mark(gas.portPlace.get(ref), "vented");
        const vent = gas.portPlace.get(ref);
        if (vent) gasVented.add(vent);
        const at = liquid.portPlace.get(ref);
        if (at) openings.add(at);
        if (at && VESSEL_ROLES.has(node.role)) airOpen.add(at);
      }
    }
  }
  // A regulator is a boundary (D66): its outlet holds its setpoint, or a sweep's
  // furthest, where its supply side has pressure, and never more than that
  // pressure; fed by a vacuum, it pulls at its setpoint the same way. With no
  // setpoint, or nothing to regulate, it relieves to its exhaust, so the
  // outlet side is vented. Repeated so a regulator fed by
  // another sees what that one gives.
  const regulators = (design.nodes ?? []).filter((node) => node.role === "regulator");
  const sweepOf = new Map(regulation.sweeps.map((sweep) => [sweep.target, sweep]));
  const settled = new Set<string>();
  const regulatorPreview: Record<string, RegulatorPreview> = {};
  for (let pass = 0; pass < regulators.length; pass++) {
    for (const node of regulators) {
      if (settled.has(node.local_id)) continue;
      const supply = gas.portPlace.get(`${node.local_id}.supply`);
      const outlet = gas.portPlace.get(`${node.local_id}.outlet`);
      if (!outlet) continue;
      const root = supply ? gas.regions.find(supply) : null;
      const fed = root !== null && (openTo.get(root)?.has("pressure") ?? false);
      // Fed by a vacuum, it regulates a vacuum: its outlet pulls at the setpoint, never more than the supply.
      const drawing = !fed && root !== null && (openTo.get(root)?.has("vacuum") ?? false);
      // A regulator fed by another that has not settled yet waits for it.
      const upstream = regulators.find((other) => !settled.has(other.local_id) && other !== node && gas.portPlace.get(`${other.local_id}.outlet`) !== undefined && gas.regions.find(gas.portPlace.get(`${other.local_id}.outlet`)!) === root);
      if (upstream && pass < regulators.length - 1) continue;
      settled.add(node.local_id);
      const name = node.label || node.local_id;
      const sweep = sweepOf.get(node.local_id);
      const asked = sweep ? profilePeak(sweep) : (regulation.setpoints[node.local_id] ?? null);
      const reads = (gas: GasState, held?: number) => (regulatorPreview[node.local_id] = { asked, ...(held !== undefined ? { held } : {}), ...(sweep ? { sweep } : {}), gas });
      if (drawing && asked !== null && asked > 0) {
        const pulls = [...gasDrive].filter(([place]) => gas.regions.find(place) === root).map(([, drive]) => drive.value);
        const available = pulls.length > 0 ? Math.max(...pulls) : null;
        const held = available === null ? asked : Math.min(asked, available);
        reads("vacuum", held);
        mark(outlet, "vacuum");
        gasVacuum.add(outlet);
        gasDrive.set(outlet, { value: held, source: name, ...(sweep ? { profile: sweep } : {}), vacuum: true });
        continue;
      }
      if (!fed || asked === null || asked <= 0) {
        reads("vented");
        mark(outlet, "vented");
        gasVented.add(outlet);
        continue;
      }
      const drives = [...gasDrive].filter(([place]) => gas.regions.find(place) === root).map(([, drive]) => drive.value);
      const available = drives.length > 0 ? Math.max(...drives) : null;
      const value = available === null ? asked : Math.min(asked, available);
      if (available !== null && asked > available) {
        findings.push({
          code: "regulator_starved",
          message: `${name} is asked for ${Math.round(asked / 1000)} kPa, but its supply gives ${Math.round(available / 1000)} kPa: it holds ${Math.round(value / 1000)} kPa`,
          refs: [node.local_id],
        });
      }
      reads("pressure", value);
      mark(outlet, "pressure");
      gasIn.add(outlet);
      gasDrive.set(outlet, { value, source: name, ...(sweep ? { profile: sweep } : {}) });
    }
  }
  // A liquid place is a gas place too; a component's may be split differently
  // (a vent's membrane), so it is found through one of its ports.
  const gasPlace = new Map<string, string>();
  for (const [ref, place] of liquid.portPlace) {
    const other = gas.portPlace.get(ref);
    if (other && !gasPlace.has(place)) gasPlace.set(place, other);
  }
  // A vessel holding liquid is at its headspace's gas: what pushes on its liquid.
  for (const id of wetVessels) gasPlace.set(nodePlace(id, 0), nodePlace(id, 0));
  const gasOf = (place: string): GasState => {
    const found = openTo.get(gas.regions.find(gasPlace.get(place) ?? place));
    if (!found) return "sealed";
    for (const state of DRIVEN) if (found.has(state)) return state;
    return "vented";
  };
  // A gas region open both to a vacuum and to the air reads as the vacuum; an
  // opening is what it itself meets. A source's port, its source; an open end,
  // the air; a vent's membrane, the nearest source or air out along its air
  // side, never back through the vent.
  const gasSources = new Map<string, GasState>();
  for (const place of gasVented) gasSources.set(place, "vented");
  for (const place of gasVacuum) gasSources.set(place, "vacuum");
  for (const place of gasIn) gasSources.set(place, "pressure");
  const exitGas = (place: string): GasState => {
    const known = openingGas.get(place);
    if (known) return known;
    if (airOpen.has(place)) return "vented";
    const node = nodes.get(elementOf(place));
    if (node?.role !== "vent") return gasOf(place);
    const own = new Set(gas.places.filter((candidate) => candidate.startsWith(`n:${node.local_id}:`)));
    const starts: string[] = [];
    for (const port of node.ports ?? []) {
      if (port.kind !== "vent") continue;
      const ref = `${node.local_id}.${port.local_id}`;
      const edge = (design.edges ?? []).find((candidate) => candidate.from_port === ref || candidate.to_port === ref);
      if (!edge) return port.is_external ? "vented" : "sealed";
      const count = spansOf(edge, (design.nodes ?? []).filter((candidate) => candidate.mount)).length;
      starts.push(spanPlace(edge.local_id, edge.from_port === ref ? 0 : count - 1));
    }
    const seen = new Set<string>([...own, ...starts]);
    let frontier = starts;
    while (frontier.length > 0) {
      const next: string[] = [];
      for (const at of frontier) {
        const found = gasSources.get(at);
        if (found) return found;
        for (const other of gas.links.get(at) ?? []) {
          if (seen.has(other)) continue;
          seen.add(other);
          next.push(other);
        }
      }
      frontier = next;
    }
    return "sealed";
  };
  // A vessel's own: a bag has no headspace, so is at the air around it; a rigid
  // one open to the air through a port of its own is at atmosphere, unless its
  // region is pushed; any other is its region's.
  const vesselDrive = (place: string): GasState => {
    const node = nodes.get(elementOf(place));
    if (node?.role === "reservoir" && !isRigid(node, templates)) return "vented";
    const region = gasOf(place);
    return airOpen.has(place) && region !== "pressure" && region !== "flow" ? "vented" : region;
  };

  // Liquid, carried over from before this step. Initial fill names elements.
  const liquidPlaces = new Map<string, string>();
  for (const place of liquid.places) {
    const carried = liquidBefore.get(place) ?? liquidBefore.get(`element:${elementOf(place)}`);
    if (carried) liquidPlaces.set(place, carried);
  }

  const moved = new Set<string>();
  // Liquid regions gas blew through: their liquid went with it, and moves no further.
  const blown = new Set<string>();
  // Lines air was drawn through from atmosphere by a vacuum: at the end of the
  // step they are open to the air they let in, at atmosphere, whatever pulls
  // beyond the liquid it met. What is shown, not what drives liquid.
  const drawnIn = new Set<string>();
  const collected = new Set<string>();
  const ranOut: { sensor?: string; from: string; to: string | null }[] = [];
  // Filters whose membrane would hold the gas once wetted: the drive stays below the bubble point.
  const holdsOnceWet = new Set<string>();
  // Liquid regions liquid moves in, and the places on its way through: for what flow meters read.
  const flowing = new Set<string>();
  const routed = new Set<string>();
  if (move) {
    // A vessel: liquid collects in it, and gas meeting its liquid first drives that liquid.
    const vessel = (place: string) => {
      const role = nodes.get(elementOf(place))?.role;
      return role === "reservoir" || role === "chamber";
    };
    // The liquid places each gas place holds: a line's spans are the same; a component's go by port.
    const held = new Map<string, Set<string>>();
    const hold = (gasAt: string, liquidAt: string) => {
      if (!held.has(gasAt)) held.set(gasAt, new Set());
      held.get(gasAt)!.add(liquidAt);
    };
    for (const [ref, place] of liquid.portPlace) {
      const other = gas.portPlace.get(ref);
      if (other) hold(other, place);
    }
    for (const place of liquid.places) if (place.startsWith("e:")) hold(place, place);
    const wetGas = (place: string) => [...(held.get(place) ?? [])].some((at) => liquidPlaces.has(at));
    const vesselGas = (place: string) => [...(held.get(place) ?? [])].some(vessel);

    // A wetted membrane: gas does not cross it, below the bubble point the manufacturer states.
    const wetMembrane = new Set<string>();
    // Filters whose membrane would hold the gas once wetted: the drive stays below the bubble point.
    holdsOnceWet.clear();
    for (const [filter, sides] of gas.membranes) {
      const node = nodes.get(filter)!;
      const point = bubblePoint(node, templates);
      const root = gas.regions.find(sides[0]);
      const drives = [...gasDrive].filter(([place]) => gas.regions.find(place) === root).map(([, drive]) => drive);
      const drive = drives.reduce<(typeof drives)[number] | null>((far, next) => (!far || next.value > far.value ? next : far), null);
      const passes = point !== null && drive !== null && drive.value >= point;
      if (!passes) holdsOnceWet.add(filter);
      const wetted = liquid.places.some((place) => place.startsWith(`n:${filter}:`) && liquidPlaces.has(place));
      if (!wetted) continue;
      if (passes && drive) {
        const name = node.label || filter;
        const reached = drive.profile ? profileReaches(drive.profile, point) : null;
        findings.push({
          code: "bubble_point",
          message: reached
            ? `The ${drive.profile!.mode === "stairs" ? "staircase" : "ramp"} on ${drive.source} reaches ${name}'s bubble point of ${Math.round(point / 1000)} kPa at ${Math.round(Math.abs(reached.value) / 1000)} kPa, ${secondsText(reached.seconds)} in: gas passes the wetted membrane from there`
            : `Gas at ${Math.round(drive.value / 1000)} kPa reaches ${name}'s bubble point of ${Math.round(point / 1000)} kPa: it passes the wetted membrane`,
          refs: [filter],
          ...(reached ? { source: drive.source, value: reached.value, point } : {}),
        });
        continue;
      }
      wetMembrane.add(`${sides[0]}|${sides[1]}`).add(`${sides[1]}|${sides[0]}`);
    }
    const gasNext = (place: string) => [...(gas.links.get(place) ?? [])].filter((other) => !wetMembrane.has(`${place}|${other}`));
    // Where liquid could go from a vessel: an empty bag, or out where the circuit is open.
    const liquidOpen = new Set(
      [...gasVented, ...gasVacuum].flatMap((place) => [...(held.get(place) ?? [])]).filter((place) => openings.has(place)),
    );
    const supplies = (place: string, swept: ReadonlySet<string>) => {
      const seen = new Set([place]);
      const queue = [place];
      while (queue.length > 0) {
        const at = queue.shift()!;
        for (const next of liquid.links.get(at) ?? []) {
          if (seen.has(next) || swept.has(next)) continue;
          seen.add(next);
          if (liquidOpen.has(next) || (vessel(next) && !liquidPlaces.has(next) && !isRigid(nodes.get(elementOf(next))!, templates))) return true;
          queue.push(next);
        }
      }
      return false;
    };

    const gasRegions = new Map<string, string[]>();
    for (const place of gas.places) {
      const root = gas.regions.find(place);
      gasRegions.set(root, [...(gasRegions.get(root) ?? []), place]);
    }
    for (const [root, region] of gasRegions) {
      const within = (places: Set<string>) => [...places].filter((place) => gas.regions.find(place) === root);
      const vacuum = within(gasVacuum);
      const vented = within(gasVented);
      // Gas driven in; or, with a vacuum pulling, air drawn in where the region is open.
      const inlets = within(gasIn).length > 0 ? within(gasIn) : vacuum.length > 0 ? vented : [];
      const outlets = [...vacuum, ...vented].filter((place) => !inlets.includes(place));
      if (inlets.length === 0 || outlets.length === 0 || !region.some(wetGas)) continue;
      // The route between them: branches that lead nowhere are pruned, as for liquid.
      const route = new Set(region);
      const ends = new Set([...inlets, ...outlets]);
      let pruned = true;
      while (pruned) {
        pruned = false;
        for (const place of [...route]) {
          if (ends.has(place)) continue;
          if (gasNext(place).filter((other) => route.has(other)).length <= 1) {
            route.delete(place);
            pruned = true;
          }
        }
      }
      // From the inlets, the gas sweeps the lines until it meets a vessel holding
      // liquid. Gas let into a vessel holding liquid, by a port of its own, meets
      // that liquid first: it drives it, below, and sweeps nothing.
      const swept = new Set(inlets.filter((place) => route.has(place) && !(vesselGas(place) && wetGas(place))));
      const met: string[] = inlets.filter((place) => route.has(place) && vesselGas(place) && wetGas(place));
      const order: string[] = [...swept];
      const queue = [...swept];
      while (queue.length > 0) {
        const at = queue.shift()!;
        for (const next of gasNext(at)) {
          if (!route.has(next) || swept.has(next) || met.includes(next)) continue;
          if (vesselGas(next) && wetGas(next)) met.push(next);
          else {
            swept.add(next);
            order.push(next);
            queue.push(next);
          }
        }
      }
      if (within(gasIn).length === 0) {
        // And the dead ends off it: open to the line the air came through.
        const queue = [...swept];
        for (const place of swept) drawnIn.add(place);
        while (queue.length > 0) {
          const at = queue.shift()!;
          for (const next of gasNext(at)) {
            if (route.has(next) || drawnIn.has(next)) continue;
            drawnIn.add(next);
            queue.push(next);
          }
        }
      }
      const sweptLiquid = new Set([...swept].flatMap((place) => [...(held.get(place) ?? [])]));
      const fluid = [...sweptLiquid].map((place) => liquidPlaces.get(place)).find(Boolean);
      if (!fluid) continue;
      // Liquid it pushed ahead of it: into a vessel it met, else the first empty one past it.
      const collector =
        met.flatMap((place) => [...(held.get(place) ?? [])]).find(vessel) ??
        order
          .slice(order.findIndex((place) => wetGas(place)))
          .flatMap((place) => [...(held.get(place) ?? [])])
          .find(vessel);
      for (const place of sweptLiquid) {
        if (vessel(place)) continue;
        if (liquidPlaces.delete(place) && place.startsWith("n:")) moved.add(elementOf(place));
      }
      if (collector && !liquidPlaces.has(collector)) liquidPlaces.set(collector, fluid);
      if (collector) {
        moved.add(elementOf(collector));
        collected.add(elementOf(collector));
      }
      // A vessel it met with somewhere for its liquid to go is a supply: that liquid moves on, below.
      // Otherwise the gas bubbles through, and what it swept moves no further.
      const supplying = met.some((place) => [...(held.get(place) ?? [])].some((at) => vessel(at) && supplies(at, sweptLiquid)));
      if (!supplying) for (const place of sweptLiquid) blown.add(liquid.regions.find(place));
    }


    // A vessel's liquid lies at its bottom: it leaves by a bottom port, never
    // by a top port or a vent, which open on its headspace. Liquid may come in
    // by any. Pairs of the vessel's place and the line's at such a port.
    const headspace = new Set<string>();
    const onLines = (design.nodes ?? []).filter((node) => node.mount);
    for (const edge of design.edges ?? []) {
      const count = spansOf(edge, onLines).length;
      for (const [end, index] of [
        [edge.from_port, 0],
        [edge.to_port, count - 1],
      ] as const) {
        const node = nodes.get(top(end));
        const port = node?.ports?.find((candidate) => `${node.local_id}.${candidate.local_id}` === end);
        const place = liquid.portPlace.get(end);
        if (node && place && vessel(place) && port && (port.kind === "vent" || port.local_id.startsWith("top")))
          headspace.add(`${place}|${spanPlace(edge.local_id, index)}`);
      }
    }
    // Liquid regions, and whether liquid can move in each.
    flowing.clear();
    const members = new Map<string, string[]>();
    for (const place of liquid.places) {
      const root = liquid.regions.find(place);
      members.set(root, [...(members.get(root) ?? []), place]);
    }
    // A running pump drives the liquid in its region, as pressure does (D45).
    // The faster first: where pumps' liquids meet, the faster one's is shown.
    const pumping = new Set(
      (design.nodes ?? [])
        .filter((node) => node.role === "pump" && states[node.local_id] === "on")
        .map((node) => node.local_id)
        .sort((a, b) => (rateOf.get(b) ?? 0) - (rateOf.get(a) ?? 0)),
    );
    // The line places either side of a running pump: its inlet's and its
    // outlet's, or for a head mounted on its tube, the spans before and after it.
    const lineAt = (ref: string): string | undefined => {
      const edge = (design.edges ?? []).find((candidate) => candidate.from_port === ref || candidate.to_port === ref);
      if (!edge) return undefined;
      return spanPlace(edge.local_id, edge.from_port === ref ? 0 : spansOf(edge, onLines).length - 1);
    };
    const pumpLines = (pump: string): { inlet?: string; outlet?: string } => {
      const node = nodes.get(pump);
      if (!node) return {};
      if (node.mount) {
        const edge = (design.edges ?? []).find((candidate) => candidate.local_id === node.mount!.edge);
        const index = edge ? spansOf(edge, onLines).findIndex((span) => span.after?.local_id === pump) : -1;
        return index < 0 ? {} : { inlet: spanPlace(edge!.local_id, index), outlet: spanPlace(edge!.local_id, index + 1) };
      }
      const ports = flowPorts(node);
      const inlet = ports.find((port) => /in/.test(port)) ?? ports[0];
      const outlet = ports.find((port) => port !== inlet);
      return { inlet: inlet ? lineAt(`${pump}.${inlet}`) : undefined, outlet: outlet ? lineAt(`${pump}.${outlet}`) : undefined };
    };
    // A running pump moves liquid one way, inlet to outlet, and nothing runs
    // back through it; nor back along the lines either side of it, as far as
    // they run unbranched: on the outlet's, liquid flows away from it, on the
    // inlet's, towards it. Pairs of places, from and to, that go against it.
    const against = new Set<string>();
    for (const pump of pumping) {
      const place = liquid.places.find((candidate) => candidate.startsWith(`n:${pump}:`));
      if (!place) continue;
      const { inlet, outlet } = pumpLines(pump);
      for (const [first, away] of [
        [outlet, true],
        [inlet, false],
      ] as const) {
        const chain = [place];
        let at = first;
        while (at && !chain.includes(at)) {
          chain.push(at);
          const links = [...(liquid.links.get(at) ?? [])];
          if (vessel(at) || links.length !== 2) break;
          at = links.find((other) => other !== chain[chain.length - 2]);
        }
        for (let index = 0; index + 1 < chain.length; index++)
          against.add(away ? `${chain[index + 1]}|${chain[index]}` : `${chain[index]}|${chain[index + 1]}`);
      }
    }
    /**
     * The vessels a running pump draws from, in the region: each found from
     * its inlet, upstream, not through the pump; the faster pump's first.
     */
    const pumpFeeds = (region: readonly string[]): string[] => {
      const feeds: string[] = [];
      for (const pump of pumping) {
        const at = pumpLines(pump).inlet;
        if (!at || !region.includes(at)) continue;
        const through = new Set(liquid.places.filter((place) => place.startsWith(`n:${pump}:`)));
        const seen = new Set<string>([at]);
        const queue = [at];
        while (queue.length > 0) {
          const here = queue.shift()!;
          if (vessel(here) && liquidPlaces.has(here)) {
            if (!feeds.includes(here)) feeds.push(here);
            break;
          }
          for (const next of liquid.links.get(here) ?? []) {
            // Upstream: liquid would come from `next` to `here`.
            if (seen.has(next) || through.has(next) || against.has(`${next}|${here}`)) continue;
            seen.add(next);
            queue.push(next);
          }
        }
      }
      return feeds;
    };
    /**
     * The vessel the region's liquid is driven from: one whose headspace gas
     * pushes on, else the one feeding the fastest running pump.
     */
    const drivenFrom = (region: readonly string[], wet: readonly string[]): string | undefined => {
      const pushedVessel = wet.find((place) => vessel(place) && !collapsibleOf(place) && vesselDrive(place) === "pressure");
      return pushedVessel ?? pumpFeeds(region)[0];
    };
    const collapsibleOf = (place: string) => {
      const node = nodes.get(elementOf(place));
      return node?.role === "reservoir" && !isRigid(node, templates);
    };
    for (const [root, region] of members) {
      if (blown.has(root)) continue;
      const wet = region.filter((place) => liquidPlaces.has(place));
      if (wet.length === 0) continue;
      // A bag collapses as it empties, so liquid can always go into or out of
      // it; a rigid vessel (D50) only where air can take its place.
      const collapsible = (place: string) => {
        const node = nodes.get(elementOf(place));
        return node?.role === "reservoir" && !isRigid(node, templates);
      };
      // What moves is what is driven: the liquid of a vessel gas pushes on, or
      // of the vessel feeding a running pump; only where neither can be told
      // is it whatever the region holds first.
      const fluid = liquidPlaces.get(drivenFrom(region, wet) ?? wet[0]!)!;
      // The liquid side of a vent's membrane: gas leaves there, liquid stops there.
      const membrane = (place: string) => nodes.get(elementOf(place))?.role === "vent";
      // Where the region is open: a vent membrane, a source or an opening.
      // Under pressure an opening is not where liquid goes (gas blowing
      // through clears the way there, above); otherwise it is, by what it
      // itself meets, so a vent on a bag drawn by a vacuum lets air in.
      const exit = (place: string) =>
        (membrane(place) || openings.has(place)) &&
        (gasOf(place) === "vented" || gasOf(place) === "vacuum") &&
        (exitGas(place) === "vented" || exitGas(place) === "vacuum");
      const pushed =
        wet.some((place) => gasOf(place) === "pressure" || gasOf(place) === "flow") ||
        region.some((place) => pumping.has(elementOf(place)));
      const evacuated = region.filter((place) => exit(place) && exitGas(place) === "vacuum");
      const followable = wet.some((place) => collapsible(place) || airOpen.has(place) || gasOf(place) === "vented" || gasOf(place) === "pressure");
      // Somewhere the liquid can go: a bag (one already holding liquid too,
      // where a vessel gas pushes on or a running pump drives liquid at it),
      // or where the region is open.
      const driven = drivenFrom(region, wet);
      const sinks = region.filter(
        (place) =>
          (collapsible(place) && (!liquidPlaces.has(place) || (driven !== undefined && driven !== place))) ||
          (!liquidPlaces.has(place) && exit(place)),
      );
      const moves = (pushed && sinks.length > 0) || (evacuated.length > 0 && followable);
      if (moves) flowing.add(root);
      if (!moves) {
        if (pushed && sinks.length === 0) {
          const where = [...new Set(wet.map(elementOf))];
          findings.push({ code: "no_way_out", message: `Liquid in ${where.slice(0, 3).join(", ")} is pressurised with nowhere to go`, refs: where });
        }
        continue;
      }
      // Where it goes: the first vessel on its way that air can leave collects
      // it; only where none is in the way does it run on to where the region
      // is open. A vessel it comes from is where it starts, not a stop.
      const room = (place: string) =>
        vessel(place) && (collapsible(place) || vesselDrive(place) === "vented" || vesselDrive(place) === "vacuum");
      const reached = new Set<string>(wet);
      const frontier = [...wet];
      while (frontier.length > 0) {
        const at = frontier.shift()!;
        for (const next of liquid.links.get(at) ?? []) {
          if (reached.has(next) || headspace.has(`${at}|${next}`) || against.has(`${at}|${next}`)) continue;
          reached.add(next);
          if (!room(next)) frontier.push(next);
        }
      }
      // The through route: prune branches that end without liquid or a way out.
      // Liquid comes from a vessel or a pump: liquid standing in a dead leg is
      // left where it is, not a place the route runs to. Only where no vessel
      // holds any is every wet place a start.
      const starts = wet.filter((place) => vessel(place) || pumping.has(elementOf(place)));
      const keep = new Set(region);
      // Drawn by a vacuum, not pushed: liquid goes only towards the vacuum;
      // where the region is open to the air, air comes in, not liquid out.
      const drawn = !pushed && evacuated.length > 0;
      const outs = drawn ? [...evacuated, ...region.filter((place) => room(place) && vesselDrive(place) === "vacuum")] : [...sinks, ...evacuated, ...region.filter(room)];
      const ends = new Set([...(starts.length > 0 ? starts : wet), ...outs.filter((place) => reached.has(place))]);
      let pruned = true;
      while (pruned) {
        pruned = false;
        for (const place of [...keep]) {
          if (ends.has(place)) continue;
          const neighbours = [...(liquid.links.get(place) ?? [])].filter((other) => keep.has(other));
          if (neighbours.length <= 1) {
            keep.delete(place);
            pruned = true;
          }
        }
      }
      // Each vessel driven keeps its own liquid, and fills what lies downstream
      // of it as far as the liquid can go; where driven liquids meet, and where
      // none can be told, the region's.
      const feeds = [...new Set([driven, ...pumpFeeds(region)].filter((place): place is string => place !== undefined))];
      const fedBy = new Map<string, Set<string>>();
      for (const feed of feeds) {
        const seen = new Set([feed]);
        const queue = [feed];
        while (queue.length > 0) {
          const at = queue.shift()!;
          if (!fedBy.has(at)) fedBy.set(at, new Set());
          fedBy.get(at)!.add(feed);
          for (const next of liquid.links.get(at) ?? []) {
            if (seen.has(next) || !keep.has(next) || headspace.has(`${at}|${next}`) || against.has(`${at}|${next}`)) continue;
            seen.add(next);
            queue.push(next);
          }
        }
      }
      const fed = new Map(
        [...keep].map((place) => {
          const from = feeds.includes(place) ? [place] : [...(fedBy.get(place) ?? [])];
          return [place, from.length === 1 ? (liquidPlaces.get(from[0]!) ?? fluid) : fluid] as const;
        }),
      );
      for (const [place, held] of fed) liquidPlaces.set(place, held);
      for (const place of keep) routed.add(place);
      for (const place of keep) if (place.startsWith("n:")) moved.add(elementOf(place));
      // What was left out is dry, closed off by the liquid around it.
      const trapped = [...new Set(region.filter((place) => !keep.has(place) && !liquidPlaces.has(place)).map(elementOf))];
      if (trapped.length > 0) {
        findings.push({
          code: "air_trapped",
          message: `Air may be trapped in ${trapped.slice(0, 4).join(", ")}${trapped.length > 4 ? ` and ${trapped.length - 4} more` : ""}`,
          refs: trapped,
        });
      }
    }
    // A step that means to run a vessel dry (D60, D62): it ends on an air
    // detector seeing air, or says it runs until a vessel is empty. Where one
    // vessel can have fed it and at most one taken it, the feed is empty, and
    // where air can follow the liquid out of it, the way from it is clear: up
    // to the detector where a pump stops as it trips, else on into the vessel
    // that took it. Where nothing can follow, the lines stay full.
    const wasWet = (place: string) => liquidBefore.has(place) || liquidBefore.has(`element:${elementOf(place)}`);
    const collapsibleVessel = (place: string) => {
      const node = nodes.get(elementOf(place));
      return node?.role === "reservoir" && !isRigid(node, templates);
    };
    const name = (places: readonly string[]) => [...new Set(places.map(elementOf))].map((id) => nodes.get(id)?.label || id).join(", ");
    const between = (from: string, to: string): string[] | null => {
      const previous = new Map<string, string | null>([[from, null]]);
      const queue = [from];
      while (queue.length > 0 && !previous.has(to)) {
        const here = queue.shift()!;
        for (const next of liquid.links.get(here) ?? []) {
          if (previous.has(next)) continue;
          previous.set(next, here);
          queue.push(next);
        }
      }
      if (!previous.has(to)) return null;
      const path: string[] = [];
      for (let place: string | null = to; place; place = previous.get(place) ?? null) path.push(place);
      return path;
    };
    for (const end of runOut) {
      const detector = end.sensor ? liquid.places.find((place) => place.startsWith(`n:${end.sensor}:`)) : undefined;
      if (end.sensor && (!detector || !liquidPlaces.has(detector))) continue;
      const anchor = detector ?? liquid.places.find((place) => place.startsWith(`n:${end.vessel}:`));
      if (!anchor) continue;
      const root = liquid.regions.find(anchor);
      const region = members.get(root) ?? [];
      const subject = end.sensor ? `${nodes.get(end.sensor)?.label || end.sensor} seeing air` : `${nodes.get(end.vessel!)?.label || end.vessel} running empty`;
      const refs = [end.sensor ?? end.vessel!];
      if (!flowing.has(root)) {
        if (end.vessel) findings.push({ code: "run_out_unclear", message: `${subject}: nothing moves its liquid in this step`, refs });
        continue;
      }
      const vessels = region.filter(vessel);
      // Where it came from: the vessel named; or the one that held liquid; or,
      // of several, the one driven, else the one not under the vacuum drawing it.
      const held = vessels.filter(wasWet);
      const driven = held.filter((place) => vesselDrive(place) === "pressure" || vesselDrive(place) === "flow");
      const feeds = end.vessel
        ? vessels.filter((place) => elementOf(place) === end.vessel)
        : held.length <= 1
          ? held
          : driven.length > 0
            ? driven
            : held.filter((place) => vesselDrive(place) !== "vacuum");
      // Where it went: another vessel with room, else out where the region is open.
      const takes = vessels.filter(
        (place) => !feeds.includes(place) && (!wasWet(place) || collapsibleVessel(place) || vesselDrive(place) === "vented" || vesselDrive(place) === "vacuum"),
      );
      if (feeds.length !== 1 || takes.length > 1) {
        findings.push({
          code: "run_out_unclear",
          message:
            feeds.length !== 1
              ? `${subject} does not say which vessel ran out: ${name(held) || "none holds liquid"}`
              : `${subject} does not say where ${name(feeds)} went: ${name(takes)}`,
          refs: [...refs, ...new Set([...held, ...takes].map(elementOf))],
        });
        continue;
      }
      const feed = feeds[0]!;
      const take = takes[0];
      const fluid = liquidPlaces.get(feed) ?? liquidBefore.get(feed) ?? liquidBefore.get(`element:${elementOf(feed)}`);
      const pumped = region.some((place) => pumping.has(elementOf(place)));
      const target = detector && (pumped || !take) ? detector : (take ?? null);
      // Whether air can take the liquid's place as the feed empties, and so
      // follow it out: past a detector that saw it, behind gas pushing on a
      // rigid vessel or into one open to the air, or from a bag's headspace.
      // A bag holding only liquid collapses: nothing follows, and the lines
      // stay full. A rigid vessel nothing can enter cannot run empty.
      const follows = detector !== undefined || (collapsibleVessel(feed) ? gassed.has(elementOf(feed)) : ["pressure", "flow", "vented"].includes(vesselDrive(feed)));
      if (!follows && !collapsibleVessel(feed)) {
        findings.push({ code: "run_out_unclear", message: `${subject}: nothing can take the place of its liquid, so it cannot run empty`, refs });
        continue;
      }
      const found = target ? between(feed, target) : [feed];
      if (!found) continue;
      const path = follows ? found : [feed];
      // Cleared from the feed on, as the air follows the liquid: as far as a
      // wetted membrane that holds the gas below its bubble point. There the
      // air stops; the filter and everything past it stay full, though the
      // liquid pushed through reaches the vessel that takes it.
      // Wetted before the step, or by the liquid that ran through it in it.
      // Not where the step ends on a detector past the membrane: it says gas gets there.
      const fromFeed = [...path].reverse();
      const sensed = detector ? fromFeed.indexOf(detector) : -1;
      const stop = fromFeed.findIndex((place, index) => {
        const next = fromFeed[index + 1];
        if (next === undefined || index < sensed) return false;
        if (wetMembrane.has(`${place}|${next}`)) return true;
        const filter = elementOf(place);
        return (
          elementOf(next) === filter &&
          place !== next &&
          holdsOnceWet.has(filter) &&
          gas.membranes.has(filter) &&
          (liquidPlaces.has(place) || liquidPlaces.has(next))
        );
      });
      const cleared = stop < 0 ? fromFeed : fromFeed.slice(0, stop).filter((place) => nodes.get(elementOf(place))?.role !== "filter");
      for (const place of cleared) if (place !== take) liquidPlaces.delete(place);
      if (take && fluid) liquidPlaces.set(take, liquidPlaces.get(take) ?? fluid);
      moved.add(elementOf(feed));
      if (take) moved.add(elementOf(take));
      ranOut.push({ ...(end.sensor ? { sensor: end.sensor } : {}), from: elementOf(feed), to: take ? elementOf(take) : null });
    }

    // A hydrophobic vent the liquid reaches.
    for (const node of design.nodes ?? []) {
      if (node.role !== "vent") continue;
      const reached = [...liquid.portPlace.entries()].some(
        ([ref, place]) => top(ref) === node.local_id && liquidPlaces.has(place),
      );
      if (reached) {
        findings.push({
          code: "vent_wetted",
          message: `Liquid can reach ${node.label || node.local_id}; a wetted hydrophobic vent stops passing gas`,
          refs: [node.local_id],
        });
      }
    }
  }

  // The gas as the step leaves it, for the drawing: a rigid vessel emptied in
  // the step no longer keeps its headspace from the lines beneath, and a
  // wetted membrane holding below its bubble point keeps its two sides apart.
  // Everything open to a source with nothing shut between reads as it.
  let leftGas = gasOf;
  if (move) {
    const wetAfter = new Set(
      (design.nodes ?? [])
        .filter((node) => (node.role === "chamber" || (node.role === "reservoir" && isRigid(node, templates))) && liquid.places.some((place) => place.startsWith(`n:${node.local_id}:`) && liquidPlaces.has(place)))
        .map((node) => node.local_id),
    );
    const heldAfter = new Set(
      [...holdsOnceWet].filter((filter) => liquid.places.some((place) => place.startsWith(`n:${filter}:`) && liquidPlaces.has(place))),
    );
    const after = topology(design, states, false, wetAfter, heldAfter);
    const open = new Map<string, Set<GasState>>();
    for (const [place, state] of marked) {
      const root = after.regions.find(place);
      if (!open.has(root)) open.set(root, new Set());
      open.get(root)!.add(state);
    }
    const placeAfter = new Map<string, string>();
    for (const [ref, place] of liquid.portPlace) {
      const other = after.portPlace.get(ref);
      if (other && !placeAfter.has(place)) placeAfter.set(place, other);
    }
    for (const id of wetAfter) placeAfter.set(nodePlace(id, 0), nodePlace(id, 0));
    leftGas = (place: string): GasState => {
      const found = open.get(after.regions.find(placeAfter.get(place) ?? place));
      if (!found) return "sealed";
      for (const state of DRIVEN) if (found.has(state)) return state;
      return "vented";
    };
  }
  const shownGas = (place: string): GasState => (drawnIn.has(gasPlace.get(place) ?? place) ? "vented" : leftGas(place));

  // Back to elements: a component is what its places are; a line by its spans.
  const nodePreview: Record<string, NodePreview> = {};
  for (const node of design.nodes ?? []) {
    const own = liquid.places.filter((place) => place.startsWith(`n:${node.local_id}:`));
    const gasPlaces = gas.places.filter((place) => place.startsWith(`n:${node.local_id}:`));
    const wet = own.map((place) => liquidPlaces.get(place)).find(Boolean) ?? null;
    const switched = switchedTo(node, states[node.local_id]);
    const common = switched ? gas.portPlace.get(`${node.local_id}.common`) : undefined;
    nodePreview[node.local_id] = {
      gas: gasPlaces.map(shownGas).find((state) => state !== "sealed") ?? "sealed",
      liquid: wet,
      passes: passes(node, states[node.local_id]),
      ...(switched ? { switched: { port: switched, gas: common ? shownGas(common) : "sealed" } } : {}),
    };
    const state = states[node.local_id];
    if (state && node.state_set && !node.state_set.includes(state)) {
      findings.push({ code: "unknown_state", message: `${node.local_id} has no state ${state}`, refs: [node.local_id] });
    }
  }
  const mounted = (design.nodes ?? []).filter((node) => node.mount);
  const edgePreview: Record<string, SpanPreview[]> = {};
  for (const edge of design.edges ?? []) {
    edgePreview[edge.local_id] = spansOf(edge, mounted).map((span, index) => {
      const place = spanPlace(edge.local_id, index);
      return { from: span.from, to: span.to, gas: shownGas(place), liquid: liquidPlaces.get(place) ?? null };
    });
  }
  const liquidRegions: Record<string, string> = {};
  for (const node of design.nodes ?? []) {
    const place = liquid.places.find((candidate) => candidate.startsWith(`n:${node.local_id}:`));
    if (place) liquidRegions[node.local_id] = liquid.regions.find(place);
  }
  for (const edge of design.edges ?? []) {
    spansOf(edge, mounted).forEach((_, index) => {
      liquidRegions[`${edge.local_id}:${index}`] = liquid.regions.find(spanPlace(edge.local_id, index));
    });
  }
  const ports: Record<string, PortPreview> = {};
  for (const source of conditions.sources ?? []) {
    const profile = profileOf.get(source.name);
    if (profile) {
      ports[top(source.port)] = { port: source.port, source: source.name, setting: "profile", kind: profile.quantity, magnitude: profilePeak(profile), profile };
      continue;
    }
    const setting = settingOf(source, sources[source.name]);
    if (!setting) continue;
    ports[top(source.port)] = {
      ...(RATED.has(source) ? { rated: true } : {}),
      port: source.port,
      source: source.name,
      setting: setting.name,
      kind: setting.kind,
      magnitude: setting.magnitude ?? null,
    };
  }
  // A port with no source, open at its end: at atmosphere, until a source is put on it.
  const sourcedPorts = new Set((conditions.sources ?? []).map((source) => source.port));
  for (const node of design.nodes ?? []) {
    if (node.role !== "port" || ports[node.local_id]) continue;
    const end = openEnd(design, node, sourcedPorts);
    if (end) ports[node.local_id] = { port: end, source: "", setting: "atmosphere", kind: "open", magnitude: null, unsourced: true };
  }
  const fluidLabels = Object.fromEntries((conditions.fluids ?? []).map((fluid) => [fluid.name, fluid.description || fluid.name]));
  const fluidColours = Object.fromEntries(
    (conditions.fluids ?? []).filter((fluid) => fluid.colour).map((fluid) => [fluid.name, fluid.colour!]),
  );
  const volumes: Record<string, number> = {};
  if (withVolumes) {
    for (const fill of conditions.initial_fill ?? []) if (typeof fill.volume === "number") volumes[fill.target] = fill.volume;
  }
  const capacities: Record<string, number> = {};
  for (const node of design.nodes ?? []) {
    if (node.role !== "reservoir" && node.role !== "chamber" && node.role !== "variable_volume_chamber") continue;
    const full = parameterValue(node, templates, "nominal_volume") ?? parameterValue(node, templates, "volume");
    if (full && full > 0) capacities[node.local_id] = full;
  }
  // What each instrument reads, only as surely as the preview knows it (see Readout).
  const readouts: Record<string, Readout> = {};
  const running = (design.nodes ?? []).filter((node) => node.role === "pump" && states[node.local_id] === "on").map((node) => node.local_id);
  const placeOf = (topology: Topology, id: string) => topology.places.find((place) => place.startsWith(`n:${id}:`));
  const pumpsIn = (root: string) => running.filter((pump) => liquid.places.some((place) => place.startsWith(`n:${pump}:`) && liquid.regions.find(place) === root));
  const gasFlows = (open: ReadonlySet<GasState>) =>
    ((open.has("pressure") || open.has("flow")) && (open.has("vented") || open.has("vacuum"))) || (open.has("vacuum") && open.has("vented"));
  type Reading = Omit<Readout, "tag" | "observes" | "limit">;
  // A pressure: what its gas region is held at, where nothing flows; under flow, only between what drives it and atmosphere.
  const pressureAt = (gasAt: string | undefined, liquidAt: string | undefined): Reading => {
    if (!gasAt) return { certainty: "unknown" };
    const root = gas.regions.find(gasAt);
    const open = openTo.get(root) ?? new Set<GasState>();
    if (open.size === 0) return { certainty: "unknown", state: "sealed" };
    const liquidRoot = liquidAt ? liquid.regions.find(liquidAt) : undefined;
    const liquidMoves = liquidRoot !== undefined && flowing.has(liquidRoot);
    // A pump drives what it reads: the preview has no pressure for that.
    if (liquidMoves && pumpsIn(liquidRoot).length > 0) return { certainty: "unknown" };
    // Under moving liquid, what drives it reaches the instrument too: the gas on every space the liquid touches.
    const roots = new Set([root]);
    if (liquidMoves) for (const place of liquid.places) if (liquid.regions.find(place) === liquidRoot) roots.add(gas.regions.find(gasPlace.get(place) ?? place));
    const drives = [...gasDrive].filter(([place]) => roots.has(gas.regions.find(place))).map(([, drive]) => drive);
    const pushed = drives.filter((drive) => !drive.vacuum).map((drive) => drive.value);
    const pulled = drives.filter((drive) => drive.vacuum).map((drive) => -drive.value);
    // A pressure or vacuum of no stated magnitude: no number to give.
    if ((open.has("pressure") && pushed.length === 0) || (open.has("vacuum") && pulled.length === 0) || open.has("flow")) return { certainty: "unknown" };
    const levels = [...pushed, ...pulled, ...(open.has("vented") ? [0] : [])];
    const flows = gasFlows(open) || liquidMoves || drives.some((drive) => drive.profile);
    if (!flows && new Set(levels).size === 1) return { certainty: "exact", value: levels[0]! };
    return { certainty: "bound", low: Math.min(0, ...levels), high: Math.max(0, ...levels) };
  };
  // A flow: none where nothing moves through it; a pump's set rate where it is on the one way through from it.
  const flowAt = (liquidAt: string | undefined, gasAt: string | undefined): Reading => {
    if (!liquidAt) return { certainty: "unknown" };
    const root = liquid.regions.find(liquidAt);
    if (!flowing.has(root) || !routed.has(liquidAt)) {
      // Dry, with gas moving past: what it reads of gas the preview cannot say.
      const open = gasAt ? openTo.get(gas.regions.find(gasAt)) : undefined;
      return !liquidPlaces.has(liquidAt) && open && gasFlows(open) ? { certainty: "unknown" } : { certainty: "exact", value: 0 };
    }
    const pumps = pumpsIn(root);
    // One way through: no place on the liquid's route where it divides.
    const branches = [...routed].some((place) => liquid.regions.find(place) === root && [...(liquid.links.get(place) ?? [])].filter((other) => routed.has(other)).length > 2);
    const rate = pumps.length === 1 ? rateOf.get(pumps[0]!) : undefined;
    return rate !== undefined && !branches ? { certainty: "exact", value: rate } : { certainty: "unknown", state: "flowing" };
  };
  const quantity = (observes: string, value: number) =>
    observes === "pressure" ? `${Number((value / 1000).toPrecision(4))} kPa` : observes === "flow_rate" ? `${Number((value * 6e7).toPrecision(4))} mL/min` : String(value);
  const symbol = { lt: "<", le: "≤", gt: ">", ge: "≥" } as const;
  for (const node of design.nodes ?? []) {
    if (node.role !== "sensor") continue;
    const observes = observesOf(node, templates);
    if (!observes) continue;
    const liquidAt = placeOf(liquid, node.local_id);
    const gasAt = placeOf(gas, node.local_id);
    const reading: Reading =
      observes === "air_in_line"
        ? { certainty: "exact", state: liquidAt && liquidPlaces.has(liquidAt) ? "liquid" : "air" }
        : observes === "pressure"
          ? pressureAt(gasAt, liquidAt)
          : observes === "flow_rate"
            ? flowAt(liquidAt, gasAt)
            : { certainty: "unknown" };
    // The step's endpoint on it: an alarm limit, and whether this step can ever meet it.
    let limit: Readout["limit"];
    for (const condition of step?.finish?.conditions ?? []) {
      const on =
        condition.kind === "sensed" && (condition.measure ?? "reading") === "reading"
          ? condition.sensor
          : condition.kind === "pressure_threshold"
            ? top(condition.at)
            : null;
      if (on !== node.local_id || (condition.kind !== "sensed" && condition.kind !== "pressure_threshold")) continue;
      const target = condition.kind === "sensed" ? condition.value : condition.pressure;
      const numeric = observes === "pressure" || observes === "flow_rate";
      const never =
        numeric &&
        (reading.certainty === "exact" && reading.value !== undefined
          ? !meets(reading.value, condition.comparison, target)
          : reading.certainty === "bound" && reading.low !== undefined && reading.high !== undefined
            ? !meets(reading.high, condition.comparison, target) && !meets(reading.low, condition.comparison, target)
            : false);
      limit = { comparison: condition.comparison, value: target, ...(never ? { unreachable: true } : {}) };
      if (never) {
        const reads = reading.value !== undefined ? quantity(observes, reading.value) : `${quantity(observes, reading.low!)} to ${quantity(observes, reading.high!)}`;
        findings.push({
          code: "endpoint_unreachable",
          message: `${node.label || node.local_id} ${symbol[condition.comparison]} ${quantity(observes, target)} can never be met in this step: it reads ${reads}`,
          refs: [node.local_id],
        });
      }
    }
    readouts[node.local_id] = { tag: instrumentTag(node, ISA_LETTERS[observes] ?? "XI"), observes, ...reading, ...(limit ? { limit } : {}) };
  }

  return {
    result: {
      states,
      sources,
      ports,
      ...(regulators.length ? { regulators: regulatorPreview } : {}),
      ...(Object.keys(readouts).length ? { readouts } : {}),
      capacities,
      inlinePorts: (design.nodes ?? []).filter((node) => isInlinePort(design, node)).map((node) => node.local_id),
      fluidLabels,
      ...(Object.keys(fluidColours).length ? { fluidColours } : {}),
      volumes,
      nodes: nodePreview,
      edges: edgePreview,
      moved: [...moved],
      ...(ranOut.length ? { ranOut } : {}),
      ...(collected.size ? { collected: [...collected] } : {}),
      regions: liquidRegions,
      findings,
    },
    liquidPlaces,
  };
}
